import React, { useEffect } from 'react';
import { useUTMClear } from '../hooks/useUTMTracking';
import { clearQuizData } from '../utils/quizDataPersistence';
import { CheckIcon } from 'lucide-react';
export function ConfirmationPage() {
  // Clear UTM tracking params on confirmation page
  useUTMClear();
  // Clear quiz data on confirmation page
  useEffect(() => {
    clearQuizData();
  }, []);
  return <div className="min-h-screen bg-white py-6 md:py-12 px-4" data-id="element-46">
      {/* Logo */}
      <div className="max-w-4xl mx-auto mb-6" data-id="element-47">
        <div className="animate-fade-in" data-id="element-48">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qmxPrQcAoJQeBtBGYhM9D6/516lion.png" alt="516 Partners Logo" className="h-16 md:h-24 mx-auto" data-id="element-49" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto" data-id="element-50">
        <div className="bg-white border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 animate-fade-in-scale" data-id="element-51">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12 animate-fade-in" data-id="element-52">
            <div className="inline-block px-3 sm:px-4 py-2 bg-green-100 border border-green-600 text-green-800 font-medium mb-4 sm:mb-6 text-sm sm:text-base" data-id="element-53">
              ✓ You're Booked
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-3 sm:mb-4 leading-tight" data-id="element-54">
              You're Booked—Here's How We'll Win the Meeting
            </h1>
            <p className="text-base sm:text-lg text-gray-600" data-id="element-55">
              Check your email for calendar details. In the meantime, please do
              the following to confirm your call:
            </p>
          </div>

          {/* Instructions Box */}
          <div className="mb-6 md:mb-8 bg-yellow-50 border-2 border-yellow-500 p-4 sm:p-6 animate-stagger-in animate-delay-100" data-id="element-56">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3" data-id="element-57">
              📧 Confirm Your Call
            </h2>
            <div className="space-y-3 text-gray-900 text-sm sm:text-base" data-id="element-58">
              <p data-id="element-59">
                <strong data-id="element-60">Step 1:</strong> Reply <strong data-id="element-61">"Yes"</strong> to our
                text or email
              </p>
              <p data-id="element-62">
                <strong data-id="element-63">Step 2:</strong> Search your inbox for{' '}
                <strong data-id="element-64">"Clinic Valuation Review Call"</strong>
              </p>
              <p data-id="element-65">
                <strong data-id="element-66">Step 3:</strong> Click{' '}
                <strong data-id="element-67">"I know the sender"</strong> and <strong data-id="element-68">"Yes"</strong>{' '}
                for the calendar invitation
              </p>
            </div>
          </div>

          {/* Calendar Confirmation Screenshot */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-200" data-id="element-69">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qXw4e8J2vBoGFXgh6maHuL/pasted-image.webp" alt="Calendar confirmation instructions - Press 'I know the sender' button in your calendar invite email" className="w-full border-2 border-gray-300 shadow-lg" data-id="element-70" />
          </div>

          {/* What to Expect */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-300" data-id="element-71">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-72">
              What We'll Cover in Your Review
            </h2>
            <div className="space-y-4" data-id="element-73">
              <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-100" data-id="element-74">
                <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900 flex-shrink-0 mt-1" data-id="element-75" />
                <div data-id="element-76">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base" data-id="element-77">
                    Pressure-test your valuation range
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base" data-id="element-78">
                    We'll walk through your numbers and identify any gaps or
                    missed opportunities that could impact your value.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-200" data-id="element-79">
                <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900 flex-shrink-0 mt-1" data-id="element-80" />
                <div data-id="element-81">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base" data-id="element-82">
                    Discuss the 14 Day Exit Readiness Challenge
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base" data-id="element-83">
                    Whether you're selling in 6 months or 2 years, we'll outline
                    the exact steps to maximize your exit value.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-300" data-id="element-84">
                <CheckIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900 flex-shrink-0 mt-1" data-id="element-85" />
                <div data-id="element-86">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base" data-id="element-87">
                    Answer your questions
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base" data-id="element-88">
                    Bring any concerns about timing, buyer types, deal
                    structure, or transition planning. We'll give you straight
                    answers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What to Bring */}
          <div className="bg-gray-50 border border-gray-300 p-4 sm:p-6 md:p-8 animate-stagger-in animate-delay-400" data-id="element-89">
            <h2 className="text-lg sm:text-xl font-serif font-bold text-gray-900 mb-4" data-id="element-90">
              What to Bring to the Call
            </h2>
            <ul className="space-y-2 text-gray-700 text-sm sm:text-base" data-id="element-91">
              <li className="flex items-start" data-id="element-92">
                <span className="mr-2 flex-shrink-0" data-id="element-93">•</span>
                <span data-id="element-94">Your P&L from last year (rough numbers are fine)</span>
              </li>
              <li className="flex items-start" data-id="element-95">
                <span className="mr-2 flex-shrink-0" data-id="element-96">•</span>
                <span data-id="element-97">Any questions about the valuation or next steps</span>
              </li>
              <li className="flex items-start" data-id="element-98">
                <span className="mr-2 flex-shrink-0" data-id="element-99">•</span>
                <span data-id="element-100">Your timeline and goals for selling</span>
              </li>
            </ul>
            <p className="text-xs sm:text-sm text-gray-600 mt-4" data-id="element-101">
              Don't worry if you don't have everything—we'll work with what you
              have.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 md:mt-12 text-center animate-fade-in" data-id="element-102">
            <p className="text-gray-600 mb-4 text-sm sm:text-base" data-id="element-103">
              Questions before the call?
            </p>
            <p className="text-gray-900 font-medium text-sm sm:text-base" data-id="element-104">
              Email us at{' '}
              <a href="mailto:ivan@516partners.com" className="underline hover:text-yellow-600 transition-colors" data-id="element-105">
                ivan@516partners.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>;
}